import {
  type FinanceEntryTypeValue,
  formatCurrency,
  formatDate,
  fromDateInput,
  startOfMonth,
  toDateInput,
} from '@crm/shared';
import { Badge, Button, Card, CardContent, cn, Input, Select, Spinner } from '@crm/ui';
import {
  AlertTriangle,
  ArrowDownCircle,
  ArrowUpCircle,
  Check,
  CircleSlash,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import {
  ConfirmDialog,
  ExportPdfButton,
  FilterBar,
  PageHeader,
  Pagination,
  PrintButton,
  StatCard,
} from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import {
  type EntryStatusFilter,
  type FinanceEntry,
  useCancelFinanceEntry,
  useDeleteFinanceEntry,
  useFinanceCategories,
  useFinanceEntries,
  usePayFinanceEntry,
  useReopenFinanceEntry,
} from '../api';
import { BillDialog } from '../components/bill-dialog';
import { EntryDialog } from '../components/entry-dialog';

const STATUS_FILTERS: { value: EntryStatusFilter; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'open', label: 'Em aberto' },
  { value: 'overdue', label: 'Vencidas' },
  { value: 'paid', label: 'Baixadas' },
  { value: 'cancelled', label: 'Canceladas' },
];

/** O texto muda com o lado da conta: quem paga nao "recebe". */
const COPY = {
  PAYABLE: {
    title: 'Contas a pagar',
    description: 'Previsao e baixa do que o salao tem para pagar.',
    icon: <ArrowDownCircle />,
    paidLabel: 'Pagas no periodo',
    payAction: 'Pagar',
    payTitle: 'Dar baixa na conta',
  },
  RECEIVABLE: {
    title: 'Contas a receber',
    description: 'Previsao e baixa do que o salao tem para receber.',
    icon: <ArrowUpCircle />,
    paidLabel: 'Recebidas no periodo',
    payAction: 'Receber',
    payTitle: 'Confirmar o recebimento',
  },
} as const;

function lastDayOfMonth(reference: Date): Date {
  return new Date(reference.getFullYear(), reference.getMonth() + 1, 0);
}

export function FinanceEntriesPage({ type }: { type: FinanceEntryTypeValue }) {
  const copy = COPY[type];
  useDocumentTitle(copy.title);

  const canWrite = useCan('finance', 'WRITE');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<EntryStatusFilter>('all');
  const [categoryId, setCategoryId] = useState('');
  // O periodo abre no mes corrente: e por ele que se paga o mes.
  const [from, setFrom] = useState(() => toDateInput(startOfMonth(new Date())));
  const [to, setTo] = useState(() => toDateInput(lastDayOfMonth(new Date())));

  const categories = useFinanceCategories({ type, onlyActive: true });
  const entries = useFinanceEntries({
    page,
    perPage,
    type,
    status,
    search: search.trim() || undefined,
    categoryId: categoryId || undefined,
    from: from ? fromDateInput(from).toISOString() : undefined,
    to: to ? fromDateInput(to).toISOString() : undefined,
  });

  const payEntry = usePayFinanceEntry();
  const reopenEntry = useReopenFinanceEntry();
  const cancelEntry = useCancelFinanceEntry();
  const deleteEntry = useDeleteFinanceEntry();

  const [editing, setEditing] = useState<FinanceEntry | null>(null);
  const [isBillDialogOpen, setBillDialogOpen] = useState(false);
  const [pendingPayment, setPendingPayment] = useState<FinanceEntry | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<FinanceEntry | null>(null);

  const mutationError =
    payEntry.error ?? reopenEntry.error ?? cancelEntry.error ?? deleteEntry.error;

  const summary = entries.data?.summary;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={copy.icon}
        title={copy.title}
        description={copy.description}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <PrintButton />
            <ExportPdfButton title={copy.title} />
            {canWrite ? (
              <Button onClick={() => setBillDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                <span>
                  Cadastrar<span className="hidden sm:inline"> conta</span>
                </span>
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        <StatCard
          title="Em aberto"
          value={formatCurrency(summary?.open.total ?? 0)}
          hint={`${summary?.open.count ?? 0} conta(s) no periodo`}
          icon={<Wallet />}
        />
        <StatCard
          title="Vencidas"
          value={formatCurrency(summary?.overdue.total ?? 0)}
          hint={`${summary?.overdue.count ?? 0} conta(s) fora do prazo`}
          icon={<AlertTriangle />}
          className={summary && summary.overdue.count > 0 ? 'border-destructive/40' : undefined}
        />
        <StatCard
          title={copy.paidLabel}
          value={formatCurrency(summary?.paid.total ?? 0)}
          hint={`${summary?.paid.count ?? 0} conta(s) baixada(s)`}
          icon={<Check />}
          className="col-span-2 sm:col-span-1"
        />
      </div>

      {mutationError ? (
        <p className="text-sm text-destructive">{toErrorMessage(mutationError)}</p>
      ) : null}

      <Card variant="flush">
        <CardContent className="space-y-6">
          <FilterBar
            className="sm:grid-cols-2 md:grid-cols-4"
            activeCount={
              (status !== 'all' ? 1 : 0) + (categoryId ? 1 : 0) + (from ? 1 : 0) + (to ? 1 : 0)
            }
            search={
              <Input
                placeholder="Buscar pela descricao"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            }
          >
            <Select
              aria-label="Situacao"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as EntryStatusFilter);
                setPage(1);
              }}
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            <Select
              aria-label="Categoria"
              value={categoryId}
              onChange={(event) => {
                setCategoryId(event.target.value);
                setPage(1);
              }}
            >
              <option value="">Todas as categorias</option>
              {(categories.data?.data ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>

            <div className="flex items-center gap-2">
              <Input
                type="date"
                aria-label="Vencimento de"
                value={from}
                onChange={(event) => {
                  setFrom(event.target.value);
                  setPage(1);
                }}
              />
              <span className="text-sm text-muted-foreground">a</span>
              <Input
                type="date"
                aria-label="Vencimento ate"
                value={to}
                onChange={(event) => {
                  setTo(event.target.value);
                  setPage(1);
                }}
              />
            </div>
          </FilterBar>

          {entries.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : entries.isError ? (
            <p className="text-sm text-destructive">{toErrorMessage(entries.error)}</p>
          ) : entries.data && entries.data.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {entries.data.data.map((entry) => (
                <li
                  key={entry.id}
                  className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{entry.description}</p>
                      {entry.status === 'PAID' ? (
                        <Badge variant="secondary">Baixada</Badge>
                      ) : entry.status === 'CANCELLED' ? (
                        <Badge variant="outline">Cancelada</Badge>
                      ) : entry.isOverdue ? (
                        <Badge variant="destructive">Vencida</Badge>
                      ) : null}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {entry.categoryName ?? 'Sem categoria'}
                      {entry.unitName ? ` - ${entry.unitName}` : ''} - vence em{' '}
                      {formatDate(entry.dueDate)}
                      {entry.paidAt ? ` - baixada em ${formatDate(entry.paidAt)}` : ''}
                    </p>
                  </div>

                  <div className="flex items-center justify-between gap-3 sm:justify-end">
                    <p
                      className={cn(
                        'font-semibold tabular-nums',
                        entry.status === 'CANCELLED' && 'text-muted-foreground line-through',
                        entry.isOverdue && 'text-destructive',
                      )}
                    >
                      {formatCurrency(entry.paidAmount ?? entry.amount)}
                    </p>

                    {canWrite ? (
                      <div className="flex items-center gap-1">
                        {entry.status === 'OPEN' ? (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              title={copy.payAction}
                              onClick={() => setPendingPayment(entry)}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Editar vencimento"
                              onClick={() => setEditing(entry)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Cancelar conta"
                              onClick={() => cancelEntry.mutate(entry.id)}
                            >
                              <CircleSlash className="h-4 w-4" />
                            </Button>
                          </>
                        ) : entry.status === 'PAID' ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Desfazer a baixa"
                            onClick={() => reopenEntry.mutate(entry.id)}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        ) : null}

                        <Button
                          variant="ghost"
                          size="icon"
                          title="Excluir"
                          onClick={() => setPendingRemoval(entry)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nenhuma conta no periodo escolhido.
            </p>
          )}

          <Pagination
            meta={entries.data?.meta}
            label="contas"
            onChange={setPage}
            perPage={perPage}
            onPerPageChange={(value) => {
              setPerPage(value);
              setPage(1);
            }}
          />
        </CardContent>
      </Card>

      <BillDialog
        open={isBillDialogOpen}
        defaultType={type}
        onClose={() => setBillDialogOpen(false)}
      />

      <EntryDialog entry={editing} onClose={() => setEditing(null)} />

      <ConfirmDialog
        open={pendingPayment !== null}
        title={copy.payTitle}
        message={`${pendingPayment?.description ?? ''} - ${formatCurrency(
          pendingPayment?.amount ?? 0,
        )} com baixa em ${formatDate(new Date())}.`}
        confirmLabel={copy.payAction}
        confirmVariant="default"
        loading={payEntry.isPending}
        onClose={() => setPendingPayment(null)}
        onConfirm={() => {
          if (pendingPayment) payEntry.mutate({ id: pendingPayment.id, input: {} });
          setPendingPayment(null);
        }}
      />

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Excluir lancamento"
        message={`${pendingRemoval?.description ?? ''} sai do financeiro e dos relatorios.`}
        loading={deleteEntry.isPending}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) deleteEntry.mutate(pendingRemoval.id);
          setPendingRemoval(null);
        }}
      />
    </div>
  );
}

export function PayablesPage() {
  return <FinanceEntriesPage type="PAYABLE" />;
}

export function ReceivablesPage() {
  return <FinanceEntriesPage type="RECEIVABLE" />;
}
